import { useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { autorise, useSession } from "@/lib/session";
import { useCompteurs } from "@/lib/compteurs";
import { Capsule } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { GROUPES, ONGLETS, titreDe } from "./menu";

export function Coquille({ children }: { children: ReactNode }) {
  const { profil, deconnexion, niveau, apercu, voirComme } = useSession();
  const navigue = useNavigate();
  const compteurs = useCompteurs();
  const [railOuvert, setRailOuvert] = useState(false);
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute("data-theme") ?? "light",
  );
  const lieu = useLocation();
  /* « Plus » ouvre le rail pour ce qu'il montre en plus des onglets. Un agent
     n'a rien de plus : ses cinq entrées sont ses cinq onglets, et un sixième
     bouton ne tiendrait pas à 390 px. Le rail lui reste accessible par le
     menu de l'en-tête, qui porte aussi sa fiche et sa sortie. */
  const aPlusQueLesOnglets =
    GROUPES.flatMap((g) => g.entrees).filter((e) => autorise(niveau, e.niveau)).length >
    ONGLETS.length;

  function basculerTheme() {
    const n = theme === "dark" ? "light" : "dark";
    setTheme(n);
    document.documentElement.setAttribute("data-theme", n);
    try {
      window.localStorage.setItem("omnix.desk.theme", n);
    } catch {
      /* stockage refusé : le choix vaut pour la session */
    }
  }

  return (
    <div className="shell">
      <nav className={`rail${railOuvert ? " open" : ""}`} aria-label="Navigation">
        <div className="rail-brand">
          <Capsule taille={13} />
        </div>

        {/* Le geste le plus fréquent est un bouton, pas une destination. */}
        <div className="rail-action">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setRailOuvert(false);
              navigue("/devis");
            }}
          >
            <Icon n="plus" cls="" />
            Nouveau devis
          </button>
        </div>

        <div className="nav">
          {GROUPES.map((g, i) => {
            const visibles = g.entrees.filter((e) => autorise(niveau, e.niveau));
            if (!visibles.length) return null;
            return (
              <div className="nav-group" key={g.label ?? `groupe-${i}`}>
                {g.label ? <div className="nav-label">{g.label}</div> : null}
                {visibles.map((e) => {
                  const n = e.compteur ? compteurs[e.compteur] : 0;
                  return (
                    /* NavLink pose lui-même aria-current="page" : la charte s'en sert. */
                    <NavLink
                      key={e.cle}
                      to={`/${e.cle}`}
                      className="nav-item"
                      onClick={() => setRailOuvert(false)}
                    >
                      <Icon n={e.icone} />
                      <span>{e.nom}</span>
                      {n > 0 ? <span className="nav-count">{n}</span> : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="rail-foot">
          {/* La direction peut regarder l'application comme un agent. L'aperçu
              ne change que l'interface — la bande en haut le dit, et le
              sélecteur reste là pour en sortir. */}
          {profil?.niveau === "admin" ? (
            <div className="voir-comme" role="group" aria-label="Vue">
              <button
                type="button"
                className="pill"
                aria-pressed={apercu === null}
                onClick={() => voirComme(null)}
              >
                Ma vue
              </button>
              <button
                type="button"
                className="pill"
                aria-pressed={apercu === "agent"}
                onClick={() => voirComme("agent")}
              >
                Vue agent
              </button>
            </div>
          ) : null}
          <div className="whoami">
            <span className="avatar">
              {(profil?.nom ?? "?")
                .split(" ")
                .map((m) => m[0] ?? "")
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </span>
            <div className="whoami-t">
              <div className="whoami-name">{profil?.nom}</div>
              <div className="whoami-mail">
                {profil?.niveau === "admin"
                  ? apercu
                    ? "Direction · en vue agent"
                    : "Direction"
                  : "Agent"}
              </div>
            </div>
            <button
              className="icon-btn whoami-sortir"
              aria-label="Se déconnecter"
              title="Se déconnecter"
              onClick={() => void deconnexion()}
            >
              <Icon n="sortir" cls="" />
            </button>
          </div>
        </div>
      </nav>

      {railOuvert ? (
        <button className="scrim" aria-label="Fermer la navigation" onClick={() => setRailOuvert(false)} />
      ) : null}

      <div className="main">
        <header className="topbar">
          <button
            className="icon-btn burger"
            aria-label="Ouvrir la navigation"
            onClick={() => setRailOuvert(!railOuvert)}
          >
            <Icon n="menu" cls="" />
          </button>
          <h1 className="view-title">{titreDe(lieu.pathname)}</h1>
          <div className="topbar-right">
            <button className="icon-btn" aria-label="Changer de thème" onClick={basculerTheme}>
              <Icon n={theme === "dark" ? "soleil" : "lune"} cls="" />
            </button>
          </div>
        </header>
        {apercu ? (
          <div className="apercu-bande" role="status">
            <Icon n="oeil" cls="" />
            <span>
              Vue d'un agent. <strong>Les écrans seuls</strong> sont bridés : la base
              répond toujours avec vos droits de direction. Pour éprouver ce qu'un agent
              reçoit vraiment, connectez-vous avec un compte agent.
            </span>
            <button type="button" className="btn btn-sm" onClick={() => voirComme(null)}>
              Revenir à ma vue
            </button>
          </div>
        ) : null}
        <main className="content">{children}</main>
      </div>

      <nav className="tabbar" aria-label="Onglets">
        {ONGLETS.map((e) => (
          <NavLink key={e.cle} to={`/${e.cle}`} aria-current={undefined}>
            <Icon n={e.icone} cls="" />
            <span>{e.nom}</span>
          </NavLink>
        ))}
        {aPlusQueLesOnglets ? (
          <button onClick={() => setRailOuvert(true)}>
            <Icon n="menu" cls="" />
            <span>Plus</span>
          </button>
        ) : null}
      </nav>
    </div>
  );
}
